import _ from 'lodash';
import Injector from 'vue-inject';
import Debug from '@/util/debug';
import Campaign from '@/models/campaign';
import { OFFLINE_DRAFT_PREFIX, ONLINE_DRAFT_PREFIX } from '@/util/constants';
import { getDraftOnlineStatus } from '@/util/helper';
import * as LocalStorage from '@/util/local-storage';
import prefilled from '@/util/prefill';


const debug = Debug('service:Draft');

const DRAFT_CACHE_EXPIRITY = 15; // seconds - TODO re-implement drafts cache with IDB

function fixApiDraft(draft) {
  // PHP returns empty object as array, we need to fix this
  if (Array.isArray(draft.details)) {
    draft.details = {};
  }

  // For some reason lat/lng are sometimes strings instead of numbers
  if (_.get(draft, 'target.locations', []).length) {
    draft.target.locations = draft.target.locations.map(l => ({
      ...l,
      lat: Number(l.lat),
      lng: Number(l.lng),
    }));
  }

  return draft;
}

function prepareForApi(draft) {
  const doNotSendKeys = ['created', 'status', 'nextStep'];
  return _.omit(_.cloneDeep(draft), doNotSendKeys);
}


export default class DraftService {
  constructor(ofs, lts, aas, sfs, as, ss, gcs, oms) {
    this.goalTypeService = ofs;
    this.locationTypesService = lts;
    this.adApiService = aas;
    this.statusFlowService = sfs;
    this.agesService = as;
    this.stepService = ss;
    this.genericConfigService = gcs;
    this.offlineModeService = oms;
  }

  getNew(prefill = true) {
    debug.info('getting new campaign');
    const camp = new Campaign();
    camp.id = null;
    camp.code = null;
    camp.target.locations = [];
    camp.target.ages = { from: 16, to: 65 };
    camp.target.radius = 0;
    camp.target.interests = [];
    camp.currentStep = null; // TODO constant
    camp.cpm = 0;
    camp.type = null;
    camp.mode = null;
    camp.facebook = {
      page: null,
      email: null,
      sameAsCustomerEmail: false,
      hasFacebookPage: true,
      creationMode: null,
    };
    // Settings step stuff
    camp.name = null;
    camp.start_date = null;
    camp.objective = null;
    camp.brief = null;
    camp.landing_page = {
      url: null,
      type: 'webpage',
    };
    camp.customer = {
      remote_id: null,
      name: null,
      company: null,
      email: null,
      phone: null,
    }; // TODO create new service
    camp.images = [];
    camp.details = {};
    camp.local_advertising_address = null; // === Customer Street Address
    camp.offer_settings = {
      title: null,
      details: null,
      code_type: null,
      code: null,
      redeem: null,
      t_c: null,
      offer_url: null,
    };
    camp.business_category_id = null;
    camp.create_landing_page = false;

    // We don't want to prefill data for existing campaigns (this object is used as base)
    if (prefill) {
      camp.prefilled = prefilled;

      _.forOwn(prefilled.data, (value, key) => {
        _.set(camp, key, value);
      });
    }

    return Promise.all([
      this.statusFlowService.getDefault().then(flow => camp.flow = flow.tag),
      this.locationTypesService.getDefault().then(types => camp.target.location_types = types),
      this.goalTypeService.getDefault().then(goal => camp.goal_type = goal.goal_type),
      this.agesService.getDefault().then(ages => camp.target.ages = ages),
      this.genericConfigService.getConfig().then(c => camp.target.distance_unit = c.distance_unit),
    ]).then(() => camp);
  }

  save(draft) {
    const data = { campaign: prepareForApi(draft) };
    return this.adApiService.saveDraft(data)
      .then(id => {
        draft.id = id;
        return this.updateLocalDraft(draft)
          .then(() => id);
      });
  }

  update(id, draft) {
    const data = { campaign: prepareForApi(draft) };
    return this.adApiService.updateDraft(id, data)
      .then(newId => {
        draft.id = newId;
        return this.updateLocalDraft(draft)
          .then(() => newId);
      });
  }

  publish(id) {
    return new Promise(resolve => resolve(this.adApiService.publishDraft(id)));
  }

  loadFromApi(id) {
    debug.info('loadFromApi', id);
    return this.adApiService.getDraft(id)
      .then(fixApiDraft)
      .then(draft => {
        if (!draft) {
          debug.warn('could not find draft: %d', id);
          throw new Error(`draft ${id} not found`);
        }
        draft.id = id;
        debug.info('got draft %d %o', id, draft);
        return this.updateLocalDraft(draft)
          .then(() => draft);
      });
  }

  getById(id, validateData = true, forceFresh = false) {
    return this.getLocalDraft(id)
      .then(cached => ((cached && !forceFresh) ? cached : this.loadFromApi(id)))
      .then(draft => {
        if (validateData) {
          return this.getNew(false)
            .then(newDraft => Object.assign({}, newDraft, draft));
        }

        return draft;
      });
  }

  getDrafts(type) {
    return this.adApiService.getUserDrafts({ type })
      .then(drafts => drafts.map(d => {
        const singleDraft = {
          ...fixApiDraft(d.draft),
          id: d.id,
          created: d.created_at,
          code: d.campaign_code,
          status: d.campaign_status,
          nextStep: this.stepService.getNextStep(d.draft.currentStep),
          creator: d.creator ? d.creator.username : null,
        };
        this.updateLocalDraft(singleDraft);
        return singleDraft;
      }));
  }

  fixSearchDrafts(drafts) {
    return drafts.map(d => {
      const singleDraft = {
        ...fixApiDraft(d.draft),
        id: d.id,
        created: d.created_at,
        code: d.campaign_code,
        status: d.campaign_status,
        nextStep: this.stepService.getNextStep(d.draft.currentStep),
        creator: d.creator ? d.creator.username : null,
      };
      return singleDraft;
    });
  }

  sendEmail(id, email) {
    return new Promise(resolve => resolve(this.adApiService.sendEmailNotification(id, email)));
  }

  makeOfflineId(id = 1) {
    const localId = `${OFFLINE_DRAFT_PREFIX}${id}`;

    return this.getLocalDraft(localId)
      .then(draft => {
        if (draft) {
          return this.makeOfflineId(id + 1);
        }
        return Promise.resolve(localId);
      });
  }

  saveOffline(id, draft) {
    return (id !== 'new' ? Promise.resolve(id) : this.makeOfflineId())
      .then(offlineId => {
        debug.info('saving offline draft', offlineId);
        draft.id = offlineId;
        return this.updateLocalDraft(draft)
          .then(() => offlineId);
      });
  }

  updateDraft(draftId, data) {
    if (!this.offlineModeService.getStatus()) {
      return this.saveOffline(draftId, data);
    }

    if (!getDraftOnlineStatus(draftId)) {
      LocalStorage.remove(draftId);
    }

    const promise = draftId > 0
      ? this.update(draftId, data)
      : this.save(data);

    return promise
      .catch(err => {
        debug.error('error while saving: %o', err);
      });
  }

  // Update local draft data, set id to be string value
  updateLocalDraft(draft) { // eslint-disable-line class-methods-use-this
    const draftId = getDraftOnlineStatus(draft.id) ? `${ONLINE_DRAFT_PREFIX}${draft.id}` : draft.id;
    debug.info('updateLocalDraft %o id:%s', draft, draftId);
    return LocalStorage.set(draftId, draft, DRAFT_CACHE_EXPIRITY);
  }

  // Get local draft data, use id as string
  getLocalDraft(id) { // eslint-disable-line class-methods-use-this
    const draftId = getDraftOnlineStatus(id) ? `${ONLINE_DRAFT_PREFIX}${id}` : id;
    debug.info('getLocalDraft, id %o', draftId);
    return LocalStorage.get(draftId);
  }
}

Injector.service('draftService', [
  'goalTypeService',
  'locationTypesService',
  'adApiService',
  'statusFlowService',
  'agesService',
  'stepService',
  'genericConfigService',
  'offlineModeService',
], DraftService);



// WEBPACK FOOTER //
// ./src/services/DraftService.js